import React from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
} from "@mui/material";
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { CalendarItem, eventOccursOnDay } from "./calendarModel";
import { useDraggableEvent } from "./DraggableEvent";
import TimeDropZone, { TimeSlot } from "./TimeDropZone";

const formatTime = (date: Date): string => {
  return format(date, 'HH:mm', { locale: sv });
};

interface DayViewProps {
  viewDate: Date;
  events: CalendarItem[];
  onEventClick: (event: React.MouseEvent<HTMLElement>, calendarItem: CalendarItem) => void;
  onEventHover?: (event: React.MouseEvent<HTMLElement>, calendarItem: CalendarItem) => void;
  onEventLeave?: () => void;
  renderEventIcon?: (event: CalendarItem) => React.ReactNode;
  showFullDay?: boolean;
}

// Extracted component so we can use the useDraggableEvent hook per event
const DraggableDayEvent: React.FC<{
  event: CalendarItem;
  position: { column: number; totalColumns: number } | undefined;
  hourHeight: number;
  rangeStart: number;
  rangeEnd: number;
  onEventClick: DayViewProps["onEventClick"];
  onEventHover?: DayViewProps["onEventHover"];
  onEventLeave?: DayViewProps["onEventLeave"];
  renderEventIcon?: DayViewProps["renderEventIcon"];
}> = ({ event, position, hourHeight, rangeStart, rangeEnd, onEventClick, onEventHover, onEventLeave, renderEventIcon }) => {
  const { setNodeRef, listeners, attributes, dragStyle } = useDraggableEvent(event);

  const startHour = event.start.getHours() + event.start.getMinutes() / 60;
  const endHour = event.end.getHours() + event.end.getMinutes() / 60;

  if (endHour <= rangeStart || startHour >= rangeEnd) return null;

  const visibleStartHour = Math.max(startHour, rangeStart);
  const visibleEndHour = Math.min(endHour, rangeEnd);
  const duration = visibleEndHour - visibleStartHour;

  const column = position?.column || 0;
  const totalColumns = position?.totalColumns || 1;
  const columnWidth = `calc((100% - 16px) / ${totalColumns})`;
  const leftPosition = `calc(8px + (100% - 16px) * ${column} / ${totalColumns})`;

  return (
    <Paper
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      elevation={2}
      sx={{
        position: "absolute",
        left: leftPosition,
        width: columnWidth,
        top: `${(visibleStartHour - rangeStart) * hourHeight}px`,
        height: `${duration * hourHeight}px`,
        bgcolor: event.color || "primary.light",
        color: "white",
        overflow: "hidden",
        p: 0.5,
        cursor: "grab",
        '&:hover': {
          bgcolor: event.color ? `${event.color}dd` : "primary.main",
          opacity: 0.9,
          zIndex: 10
        },
        minWidth: '100px',
        fontSize: totalColumns > 2 ? '0.875rem' : '1rem',
        ...dragStyle,
      }}
      onClick={(e) => onEventClick(e, event)}
      onMouseEnter={onEventHover ? (e) => onEventHover(e, event) : undefined}
      onMouseLeave={onEventLeave}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        {renderEventIcon && renderEventIcon(event)}
        <Typography variant="body2" fontWeight="bold" noWrap>
          {event.title}
        </Typography>
      </Box>

      <Typography variant="caption" noWrap>
        {formatTime(event.start)} - {formatTime(event.end)}
      </Typography>

      {event.location && (
        <Typography variant="caption" noWrap sx={{ display: "block", mt: 0.5 }}>
          📍 {event.location}
        </Typography>
      )}
    </Paper>
  );
};

const DayView: React.FC<DayViewProps> = ({
  viewDate,
  events,
  onEventClick,
  onEventHover,
  onEventLeave,
  renderEventIcon,
  showFullDay = false
}) => {
  const getEventsForDay = (day: Date): CalendarItem[] => {
    return events.filter(event => eventOccursOnDay(event, day));
  };

  const eventsOverlap = (event1: CalendarItem, event2: CalendarItem): boolean => {
    return event1.start < event2.end && event2.start < event1.end;
  };

  const calculateEventPositions = (events: CalendarItem[]) => {
    const positioned: Map<string, { column: number; totalColumns: number }> = new Map();

    const sortedEvents = [...events].sort((a, b) => {
      const startDiff = a.start.getTime() - b.start.getTime();
      if (startDiff !== 0) return startDiff;
      return (b.end.getTime() - b.start.getTime()) - (a.end.getTime() - a.start.getTime());
    });

    const groups: CalendarItem[][] = [];

    sortedEvents.forEach(event => {
      let placed = false;
      for (const group of groups) {
        if (group.some(groupEvent => eventsOverlap(event, groupEvent))) {
          group.push(event);
          placed = true;
          break;
        }
      }
      if (!placed) {
        groups.push([event]);
      }
    });

    groups.forEach(group => {
      const columns: CalendarItem[][] = [];
      group.forEach(event => {
        let columnIndex = 0;
        while (columnIndex < columns.length) {
          if (!columns[columnIndex].some(colEvent => eventsOverlap(event, colEvent))) {
            break;
          }
          columnIndex++;
        }
        if (columnIndex < columns.length) {
          columns[columnIndex].push(event);
        } else {
          columns.push([event]);
        }
      });

      const totalColumns = columns.length;
      columns.forEach((column, columnIndex) => {
        column.forEach(event => {
          positioned.set(event.id, { column: columnIndex, totalColumns });
        });
      });
    });

    return positioned;
  };

  const rangeStart = showFullDay ? 0 : 6;
  const rangeEnd = showFullDay ? 24 : 19;
  const hours = Array.from({ length: rangeEnd - rangeStart }, (_, i) => i + rangeStart);
  const hourHeight = showFullDay ? 50 : 80;
  const slotHeight = hourHeight / 2;
  const eventsForDay = getEventsForDay(viewDate);
  const allDayEvents = eventsForDay.filter(event => event.isAllDay);
  const timedEvents = eventsForDay.filter(event => !event.isAllDay);
  const eventPositions = calculateEventPositions(timedEvents);

  const timeSlots: TimeSlot[] = [];
  for (const hour of hours) {
    for (const minute of [0, 30]) {
      timeSlots.push({
        id: `day-${format(viewDate, 'yyyy-MM-dd')}-${hour}-${minute}`,
        date: viewDate,
        hour,
        minute,
      });
    }
  }

  return (
    <Box sx={{ height: "calc(100vh - 200px)", overflow: "auto" }}>
      {/* Heldagsrad ovanför timgriden */}
      {allDayEvents.length > 0 && (
        <Grid container sx={{ borderBottom: "2px solid #e0e0e0" }}>
          <Grid item xs={1} sx={{ pr: 1, textAlign: "right", pt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Heldag
            </Typography>
          </Grid>
          <Grid item xs={11} sx={{ py: 0.5, pl: 1 }}>
            {allDayEvents.map(event => (
              <Paper
                key={event.id}
                elevation={1}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  height: 26,
                  mb: 0.5,
                  px: 1,
                  mr: 1,
                  bgcolor: event.color || "primary.light",
                  color: "white",
                  overflow: "hidden",
                  cursor: "pointer",
                  '&:hover': {
                    bgcolor: event.color ? `${event.color}dd` : "primary.main",
                    opacity: 0.9,
                  },
                }}
                onClick={(e) => onEventClick(e, event)}
                onMouseEnter={onEventHover ? (e) => onEventHover(e, event) : undefined}
                onMouseLeave={onEventLeave}
              >
                {renderEventIcon && renderEventIcon(event)}
                <Typography variant="body2" fontWeight="bold" noWrap>
                  {event.title}
                </Typography>
                {event.location && (
                  <Typography variant="caption" noWrap>
                    📍 {event.location}
                  </Typography>
                )}
              </Paper>
            ))}
          </Grid>
        </Grid>
      )}
      <Grid container>
        <Grid item xs={1}>
          {hours.map(hour => (
            <Box
              key={hour}
              sx={{
                height: `${hourHeight}px`,
                borderBottom: "1px solid #e0e0e0",
                pr: 1,
                textAlign: "right"
              }}
            >
              <Typography variant="caption">{`${hour}:00`}</Typography>
            </Box>
          ))}
        </Grid>

        <Grid item xs={11} sx={{ position: "relative" }}>
          {/* Drop zone grid */}
          {timeSlots.map((slot) => (
            <TimeDropZone key={slot.id} slot={slot} height={slotHeight}>
              <Box
                sx={{
                  height: "100%",
                  borderBottom: slot.minute === 0 ? "1px solid #e0e0e0" : "1px dashed #f0f0f0",
                  borderLeft: "1px solid #e0e0e0",
                }}
              />
            </TimeDropZone>
          ))}

          {/* Render draggable events */}
          {timedEvents.map(event => (
            <DraggableDayEvent
              key={event.id}
              event={event}
              position={eventPositions.get(event.id)}
              hourHeight={hourHeight}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              onEventClick={onEventClick}
              onEventHover={onEventHover}
              onEventLeave={onEventLeave}
              renderEventIcon={renderEventIcon}
            />
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

export default DayView;
